// Shared visual tokens for the dark dashboard surfaces (HomePage, About,
// Contact). Extracted so overlay cards — graduation notice, PWA install
// prompts — stay in step with the pages behind them instead of drifting into
// their own look.
import type { CSSProperties } from 'react';

export const BG = '#0A0A0A';
export const ACCENT = '#14B8A6';
export const ACCENT_BRIGHT = '#2DD4BF';

export const FONT_DISPLAY = "'Instrument Serif',serif";
export const FONT_BODY = "'Outfit',sans-serif";
export const FONT_MONO = "'JetBrains Mono',monospace";

/** Body copy tiers, matching the opacity ramp used across the dashboard. */
export const TEXT = {
  primary: '#fff',
  secondary: 'rgba(255,255,255,.5)',
  muted: 'rgba(255,255,255,.4)',
  faint: 'rgba(255,255,255,.3)',
  ghost: 'rgba(255,255,255,.2)',
};

export const HAIRLINE = 'rgba(255,255,255,.07)';

/** Neutral card: the default surface for list rows and secondary panels. */
export const panel: CSSProperties = {
  background: 'rgba(255,255,255,.03)',
  border: `1px solid ${HAIRLINE}`,
  borderRadius: 14,
};

/** Accent card: teal gradient used for hero/greeting panels. */
export const accentPanel: CSSProperties = {
  background: 'linear-gradient(135deg,rgba(20,184,166,.12) 0%,rgba(6,182,212,.07) 100%)',
  border: '1px solid rgba(20,184,166,.18)',
  borderRadius: 16,
};

/** Uppercase monospace micro-label, e.g. "PAKISTAN TIME". */
export const microLabel: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 9,
  letterSpacing: '.14em',
  color: 'rgba(20,184,166,.7)',
  textTransform: 'uppercase',
};

/** Soft radial bloom placed behind card content. Always pointer-events:none. */
export function glow(size = 160, opacity = 0.18): CSSProperties {
  return {
    position: 'absolute',
    width: size,
    height: size,
    background: `radial-gradient(circle,rgba(20,184,166,${opacity}) 0%,transparent 65%)`,
    pointerEvents: 'none',
  };
}

/** Small teal pill, as used for "In 25 min" badges. */
export const pill: CSSProperties = {
  background: 'rgba(20,184,166,.15)',
  border: '1px solid rgba(20,184,166,.25)',
  borderRadius: 20,
  padding: '4px 10px',
  fontFamily: FONT_MONO,
  fontSize: 10,
  letterSpacing: '.08em',
  color: ACCENT,
};

export const primaryButton: CSSProperties = {
  background: ACCENT,
  border: '1px solid ' + ACCENT,
  color: '#04211E',
  borderRadius: 10,
  padding: '12px 18px',
  fontFamily: FONT_BODY,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'background .2s, border-color .2s',
};

export const ghostButton: CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,.12)',
  color: 'rgba(255,255,255,.6)',
  borderRadius: 10,
  padding: '12px 18px',
  fontFamily: FONT_BODY,
  fontSize: 13,
  fontWeight: 400,
  cursor: 'pointer',
  transition: 'background .2s, border-color .2s, color .2s',
};

/** Outlined teal button, matching the "Back to Home" affordance on About. */
export const accentOutlineButton: CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(20,184,166,.5)',
  color: ACCENT,
  borderRadius: 10,
  padding: '10px 14px',
  fontFamily: FONT_BODY,
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  transition: 'background .2s, border-color .2s',
};
